import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Button } from './index';
import { api } from '../services/api';
import type { PropertySummary, FlatSearch } from '../types/api';

interface CompleteSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  searchId: string;
  properties: PropertySummary[];
  onComplete: (search: FlatSearch) => void;
}

export function CompleteSearchModal({ isOpen, onClose, searchId, properties, onComplete }: CompleteSearchModalProps) {
  const { t } = useTranslation();
  const rentedOrApplied = properties.filter((p) => p.status === 'rented' || p.status === 'applied');
  const preSelected = rentedOrApplied.find((p) => p.status === 'rented');

  const [selectedPropertyId, setSelectedPropertyId] = useState<string | null>(preSelected?.id ?? null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const updated = await api.updateSearch(searchId, {
        status: 'completed',
        ...(selectedPropertyId ? { rentedPropertyId: selectedPropertyId } : {}),
      });
      onComplete(updated);
      onClose();
    } catch {
      setError(t('app.completeSearchModal.failedError'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('app.completeSearchModal.modalTitle')}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <p>{t('app.completeSearchModal.description')}</p>

        {rentedOrApplied.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label className="input-label">{t('app.completeSearchModal.propertyLabel')}</label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="radio"
                name="rented-property"
                checked={selectedPropertyId === null}
                onChange={() => setSelectedPropertyId(null)}
              />
              <span>{t('app.completeSearchModal.noneOption')}</span>
            </label>
            {rentedOrApplied.map((p) => (
              <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="rented-property"
                  checked={selectedPropertyId === p.id}
                  onChange={() => setSelectedPropertyId(p.id)}
                />
                <span>{p.address}</span>
                {p.status === 'rented' && (
                  <span className="badge badge-success" style={{ fontSize: '11px' }}>{t('app.completeSearchModal.rentedBadge')}</span>
                )}
              </label>
            ))}
          </div>
        )}

        {error && <p style={{ color: 'var(--error)', fontSize: '14px' }}>{error}</p>}

        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" size="sm" onClick={handleConfirm} disabled={submitting}>
            {submitting ? t('app.completeSearchModal.completing') : t('app.completeSearchModal.completeButton')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
